// Hover preview for an inline card link (`[name](card:id)` pill). The
// deck notes panel and the global chat both render these, so the rect
// math and the floating card image live here once.
import { createPortal } from 'preact/compat';
import { isFoilFinish } from '../utils/sorcery/foil.js';
import { foilZonesForCard } from '../utils/sorcery/foilZones.js';
import { DEFAULT_FOIL_PARAMS } from '../utils/sorcery/foilParams.js';
import FoilPreview from './FoilPreview';
import { RADIUS } from '../lib/medievalTheme';

// Canvas-local rect of the hovered pill. position:fixed inside the panel
// is scoped to the .app-canvas containing block (transform: translateZ(0)),
// so viewport coords would land in the wrong spot at any canvas offset.
export function cardLinkAnchorRect(element) {
  if (!element || !element.getBoundingClientRect) return null;
  const viewportRect = element.getBoundingClientRect();
  const canvasEl = typeof document !== 'undefined' ? document.querySelector('.app-canvas') : null;
  const canvasRect = canvasEl
    ? canvasEl.getBoundingClientRect()
    : { left: 0, top: 0, width: typeof window !== 'undefined' ? window.innerWidth : 1600, height: typeof window !== 'undefined' ? window.innerHeight : 900 };
  const rect = {
    left: viewportRect.left - canvasRect.left,
    right: viewportRect.right - canvasRect.left,
    top: viewportRect.top - canvasRect.top,
    bottom: viewportRect.bottom - canvasRect.top,
    canvasWidth: canvasRect.width,
    canvasHeight: canvasRect.height,
  };
  return rect;
}

export default function CardLinkPreview({ card, rect }) {
  if (!card || !rect) return null;
  const printing = card.printings?.[0];
  const imageUrl = printing?.image_url;
  if (!imageUrl) return null;

  // Card-builder sized preview: 14cqw wide for normal cards
  // (portrait), 19cqw for sites (landscape after rotating). Just
  // the card image with a drop shadow. No panel chrome.
  const isFoil = isFoilFinish(printing?.foiling);
  const isSite = !!card.played_horizontally;
  const widthCqw = isSite ? 19 : 14;
  const heightCqw = isSite ? widthCqw * (63 / 88) : widthCqw * (88 / 63);

  // Anchor to the right of the pill when there's room; flip to the
  // left otherwise. Top edge aligns roughly with the pill row.
  const cqw = rect.canvasWidth / 100;
  const widthPx = widthCqw * cqw;
  const heightPx = heightCqw * cqw;
  const gap = 0.75 * cqw;
  const placeRight = rect.right + widthPx + gap + 8 < rect.canvasWidth;
  const left = placeRight
    ? rect.right + gap
    : Math.max(8, rect.left - widthPx - gap);
  const top = Math.max(
    8,
    Math.min(rect.canvasHeight - heightPx - 8, rect.top - heightPx / 2 + (rect.bottom - rect.top) / 2),
  );

  // Sites store their art portrait (63:88) but are played landscape.
  // we wrap the img in a landscape frame and rotate the (portrait)
  // image 90° to fill it, mirroring the deck-builder sidebar
  // preview. Normal cards just render the image directly.
  const node = isSite ? (
    <div
      style={{
        position: 'fixed',
        top,
        left,
        width: `${widthCqw}cqw`,
        height: `${heightCqw}cqw`,
        zIndex: 270,
        pointerEvents: 'none',
        borderRadius: RADIUS.cardLandscape,
        overflow: 'hidden',
        boxShadow: '0 24px 48px rgba(0,0,0,0.75), 0 8px 16px rgba(0,0,0,0.55)',
      }}
    >
      <img
        src={imageUrl}
        alt={card.name}
        draggable={false}
        style={{
          position: 'absolute',
          width: `${heightCqw}cqw`,
          height: `${widthCqw}cqw`,
          top: `calc((${heightCqw}cqw - ${widthCqw}cqw) / 2)`,
          left: `calc((${widthCqw}cqw - ${heightCqw}cqw) / 2)`,
          transform: 'rotate(90deg)',
          transformOrigin: 'center center',
          objectFit: 'cover',
        }}
      />
      {isFoil ? (
        <FoilPreview imageUrl={imageUrl} zones={foilZonesForCard(card)} params={DEFAULT_FOIL_PARAMS} rotate90 />
      ) : null}
    </div>
  ) : (
    <div
      style={{
        position: 'fixed',
        top,
        left,
        width: `${widthCqw}cqw`,
        aspectRatio: '63 / 88',
        borderRadius: RADIUS.card,
        overflow: 'hidden',
        boxShadow: '0 24px 48px rgba(0,0,0,0.75), 0 8px 16px rgba(0,0,0,0.55)',
        zIndex: 270,
        pointerEvents: 'none',
      }}
    >
      <img
        src={imageUrl}
        alt={card.name}
        draggable={false}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
      {isFoil ? (
        <FoilPreview imageUrl={imageUrl} zones={foilZonesForCard(card)} params={DEFAULT_FOIL_PARAMS} />
      ) : null}
    </div>
  );

  // Portal to .app-canvas. The notes panel's transform makes it
  // a containing block, which would scope this position:fixed to
  // the panel instead of the canvas. Anchor coords are
  // canvas-local, so the preview must live under the canvas.
  // Portal to .app-canvas. A transformed host panel would otherwise scope
  // this position:fixed to itself; anchor coords are canvas-local.
  const canvas = typeof document !== 'undefined' ? document.querySelector('.app-canvas') : null;
  return canvas ? createPortal(node, canvas) : node;
}
